/* eslint-disable no-loop-func */
import React, { useContext, useEffect, useState } from 'react';
import { getParameter } from '../../components/BotComponents/Commons';
import CanvasFlow from '../../components/CanvasFlow/CanvasFlow';
import { createEdge, createNode } from '../../components/CanvasFlow/Helpers';
import componentsType from '../../components/Component/ComponentType';
import ConfigurationsBox from '../../components/ConfigurationsBox/ConfigurationsBox';
import DialogNotification from '../../components/DialogNotification/DialogNotification';
import Loading from '../../components/Loading/Loading';
import Dialog from '../../components/WatsonComponents/DialogNode/DialogNode';
import BotContext from '../../context/FlowContext';
import WatsonContext from '../../context/WatsonContext';
import WatsonConfigurations from '../WatsonConfigurations/WatsonConfigurations';
import SaveContext from '../../context/SaveContext';
import { CHANNEL_FROM_WATSON } from '../../dto/DialogDTO';

const WatsonFlow = (props) => {
  const watsonContext = useContext(WatsonContext);
  const botContext = useContext(BotContext);
  const saveContext = useContext(SaveContext);

  const [flow, setFlow] = useState({});
  const [openedFolders, setOpenedFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDialog, setSelectedDialog] = useState(null);
  const [createdDialogId, setCreatedDialogId] = useState(null);
  const [nodeOnFocus, setNodeOnFocus] = useState(null);
  const [isWatsonLoadingFailed, setIsWatsonLoadingFailed] = useState(false);

  /**
   * Abre uma pasta com id (folderId) se ela
   * estiver fechada ou fecha se ela estiver aberta
   * Se justOpen for true, apenas abre
   * @param {*} folderId
   */
  const openOrCloseFolders = (folderId, justOpen = false) => {
    const openedFoldersState = openedFolders ? [...openedFolders] : [];
    let folder = watsonContext.dialogs[folderId];
    if (!folder || !folder.id) return null;
    const index = openedFoldersState.indexOf(folder.id);
    if (index !== -1) {
      if (!justOpen) openedFoldersState.splice(index, 1);
    } else {
      do {
        if (!openedFoldersState.includes(folder.id)) openedFoldersState.push(folder.id);
        folder = watsonContext.dialogs[folder.parent];
      }
      while (folder);
    }

    setOpenedFolders(openedFoldersState);
  };

  /** Manipulador da ação de adição de nó no fluxo do Watson.
   *
   *  Ativa o Loading e cria o dialogo no contexto. Quando a API responder,
   *  o Loading é desativado e o id do dialogo criado é registrado no estado createdDialogId
   *  Se o dialogo tiver contido em uma pasta, ela é adicionada no estado openOrCloseFolders
   */
  const addNodeFlowHandler = (node, edge) => {
    setLoading(true);
    setFlow({});
    const newDialog = node.data;
    const dialogSource = edge.source;
    watsonContext.createDialog(newDialog, dialogSource, (createdDialog) => {
      setSelectedDialog(null);
      setLoading(false);
      if (createdDialog.parent) openOrCloseFolders(createdDialog.parent, true);
      setCreatedDialogId(createdDialog.id);
    });
  };

  /** Manipulador da ação de ediçao de nó no fluxo do Watson.
   *
   *  Se o dialogo for um pasta, ela adicionada no estado openOrCloseFolders
   *  e no próximo ciclo, a pasta é mostrada aberta */
  const editDialogHandler = (id) => {
    const node = flow[id];
    if (node.data.typeElement.id === componentsType.WATSON_DIALOG_FOLDER.id) {
      openOrCloseFolders(id);
    }
    if (node.data.typeElement.id === componentsType.WATSON_ROOT.id) {
      setSelectedDialog(null);
    } else {
      setSelectedDialog(node);
    }
  };

  const createFirstNode = (xPosition, yPosition, componentSelected) => {
    const firstNode = createNode(componentsType.WATSON_ROOT, xPosition, yPosition);
    firstNode.data = {
      ...firstNode.data,
      isValidFlow: true,
      label: componentSelected.label,
      parameters: componentSelected.parameters,
      environmentConfigurationId: componentSelected.environmentConfigurationId,
    };
    return firstNode;
  };

  /**
   * Cria um nó do fluxo do Watson, a partir de um no do fluxo do watson no contexto
   *
   * @param {*} dialog
   * @param {*} xPosition
   * @param {*} yPosition
   * @returns no do fluxo do Watson
   */
  const createWatsonFlowNode = (dialog, xPosition, yPosition) => {
    const componentType = dialog.type !== 'folder' ? componentsType.WATSON_DIALOG : componentsType.WATSON_DIALOG_FOLDER;
    const node = createNode(componentType, xPosition, yPosition);
    node.id = dialog.id;
    node.order = dialog.id;
    node.data.id = dialog.id;
    node.data.isValid = true;
    node.data.isValidFlow = true;
    node.data.label = dialog.title;
    node.data.untitlePlaceholder = dialog.untitlePlaceholder;
    node.data.dialogConditions = dialog.dialogConditions;
    node.data.intent = dialog.dialogConditions ? dialog.dialogConditions.find((c) => c.type === 'intenção') : null;
    node.data.intent = node.data.intent ? node.data.intent.object : '';
    node.data.outputs = dialog.outputs;
    node.data.type = dialog.type;
    node.data.parent = dialog.parent;
    node.data.nextSibling = dialog.nextSibling;
    node.data.previousSibling = dialog.previousSibling;
    node.data.nextStep = dialog.nextStep;
    node.data.slots = dialog.slots;
    node.data.enabledChannels = dialog.enabledChannels;
    node.data.tag = dialog.enabledChannels.length < Object.values(CHANNEL_FROM_WATSON).length ? dialog.enabledChannels.join(', ') : null;
    if (!node.data.nextSibling && !node.data.parent) node.lastNodeRoot = true;
    return node;
  };

  const defineLastNodeInFlow = (flowToChange) => {
    const flowChangedList = Object.values(flowToChange).filter((i) => i.data);
    let lastNode = flowChangedList.find((node) => node.lastNodeRoot);
    let childreOfLastNode = flowChangedList.filter(
      (node) => node.data.parent && node.data.parent === lastNode.id,
    );

    while (childreOfLastNode.length !== 0) {
      lastNode = childreOfLastNode.find((node) => !node.data.nextSibling);
      childreOfLastNode = flowChangedList.filter(
        (node) => node.data.parent && node.data.parent === lastNode.id,
      );
    }

    const flowChanged = { ...flowToChange };
    flowChanged[lastNode.id] = {
      ...lastNode,
      lastNode: true,
    };

    return flowChanged;
  };

  const createWatsonEdgeFlow = (sourceId, targetId, isFistDialog) => {
    const edge = createEdge(sourceId, targetId);
    edge.targetHandle = isFistDialog ? 'targetHandle' : 'targetHandleTop';
    return edge;
  };

  /** Inicializa o fluxo de conversação do Watson.
   *
   *  Cria um nó no estado flow para cada dialogo ou pastas carregados no contexto do watson */
  const initWatsonFlow = () => {
    const { componentSelected } = props;
    const { dialogs } = watsonContext;
    let flowState = {};

    if (!dialogs) return;

    let yPosition = 0;
    let xPosition = 20;
    const firstNode = createFirstNode(xPosition, yPosition, componentSelected);
    let sourceId = firstNode.id;
    let isFistDialog = true;
    flowState[firstNode.id] = firstNode;

    /* Perrcore todos os nós carregados no contexto */
    Object.keys(dialogs).forEach((id) => {
      xPosition = 150;
      const dialog = dialogs[id];

      /** se o dialog tiver pai (contido em pasta),
      essa pasta precisa esta incluida no estado openedFolders
      para esse dialogo se adicionado no fluxo */
      const pastaDoDialogo = dialog.parent;
      if (pastaDoDialogo) {
        if (!openedFolders) return;
        if (!openedFolders.includes(pastaDoDialogo)) return;
        if (!flowState[pastaDoDialogo]) return;
        xPosition = flowState[pastaDoDialogo].position.x + 60;
      }

      yPosition += 111;
      const node = createWatsonFlowNode(dialog, xPosition, yPosition);
      node.flowLength = dialogs.length;
      flowState[node.id] = node;

      const edge = createWatsonEdgeFlow(sourceId, id, isFistDialog);
      flowState[edge.id] = edge;

      sourceId = id;
      if (isFistDialog) isFistDialog = false;
    });

    flowState = defineLastNodeInFlow(flowState);

    setFlow(flowState);
  };

  const watsonLoadingFailHandler = () => {
    setLoading(false);
    setIsWatsonLoadingFailed(true);
  };

  const watsonLoadingSuccessHandler = () => {
    setLoading(false);
  };

  /** Mostra a caixa de configuração do Watson com a configuração do
   *  componente ou a configuração de um nó/pasta do fluxo do Watson
   *
   * Se não existir componente selecionado, ou ele já tiver sido removido,
   * a caixa de configuração é renderizada vazia represetando o fechamento da caixa */
  const showWatsonConfigurations = () => {
    if (!props.componentSelected) {
      return <ConfigurationsBox />;
    }

    const content = selectedDialog ? (
      <Dialog
        watsonNameInstance={props.componentSelected.label}
        selectedDialog={selectedDialog}
        deselectDialog={() => { setSelectedDialog(null); }}
        openOrCloseFolders={openOrCloseFolders}
        setNodeOnFocus={setNodeOnFocus}
      />
    ) : (
      <WatsonConfigurations
        componentsListNames={props.componentsListNames}
        updateWatsonComponent={props.updateWatsonComponent}
        componentSelected={props.componentSelected}
      />
    );

    return (
      <ConfigurationsBox>
        {content}
      </ConfigurationsBox>
    );
  };

  /** Adiciona manipuladores de ação nos nó do fluxo,
   *  para permitir que seja possivel adicionar novos nó, ou editar os existentes  */
  const setFlowNodeActionHandlers = () => {
    const flowState = flow;
    Object.keys(flowState).forEach(
      (key) => {
        flowState[key].data = {
          ...flow[key].data,
          addNodeFlow: addNodeFlowHandler,
          editComponent: editDialogHandler,
          createNode,
          createEdge,
        };
      },
    );
  };

  const loadIntentEntitiesDialogs = () => {
    watsonContext.fetchIntents(
      () => watsonContext.fetchEntities(
        () => watsonContext.fetchDialogs(
          watsonLoadingSuccessHandler,
          watsonLoadingFailHandler,
        ),
        watsonLoadingFailHandler,
      ),
      watsonLoadingFailHandler,
    );
  };

  /** Manipulador da ação de seleção do nó no fluxo */
  const selectNodeHandler = (node) => {
    let selectedDialogId = selectedDialog ? selectedDialog.id : null;

    if (node && node[0]) {
      if (node[0].type !== 'NodeFlow') return null;

      if (createdDialogId) {
        setCreatedDialogId(null);
      } else if (!selectedDialogId || selectedDialogId !== node[0].id) {
        selectedDialogId = node[0].id;
        const nextSelectedDialog = flow[selectedDialogId];

        // Mostrar configurações do Watson
        if (nextSelectedDialog.data.typeElement.id === componentsType.WATSON_ROOT.id) {
          setSelectedDialog(null);
        } else {
          // Mostrar dialogo/pasta
          setSelectedDialog(nextSelectedDialog);
        }
      }
    }
  };

  /** Mostra mensagem de notificação alertando falha na configuração do Watson */
  const showWatsonLoadingFailed = () => (
    <DialogNotification
      title="Não foi possivel conectar ao Watson"
      content={(
        <>
          <p>
            As configurações do Watson são invalidas.
            <br />
            Solicite ao administrador do bot a
            revisão das configurações e tente novamente
          </p>
        </>
      )}
      okAction={() => {
        setIsWatsonLoadingFailed(false);
        watsonContext.reset();
        setFlow({});
      }}
    />
  );

  /** Efeito depois que o componente montou. Reinicia o contexto do watson */
  useEffect(() => () => {
    watsonContext.reset();
  }, []);

  /** Efeito depois que o componente watson foi selecionado no fluxo de conversa.
   *  Mostra breadcrumbs e Inicializa as configurações do Watson */
  useEffect(() => {
    props.renderBreadcrumbs();
    setLoading(true);
    watsonContext.initialize({
      chatBot: botContext.activeBotId,
      flow: botContext.flow.id,
      bot: props.componentSelected.id,
      environmentConfiguration: props.componentSelected.environmentConfigurationId,
      workspaceId: getParameter(props.componentSelected.parameters, 'watson.skill.id').value,
    });
  }, [props.componentSelected]);

  /** Efeito depois que o contexto do watson foi inicializado.
   *  Carrega intents, entities e dialogos */
  useEffect(() => {
    if (watsonContext.initialized) loadIntentEntitiesDialogs();
  }, [watsonContext.initialized]);

  /** Efeito depois que os dialogos foram alterado no contexto ou
   *  alguma pasta foi aberta ou fechada */
  useEffect(() => {
    if (watsonContext.dialogs || Object.keys(flow).length > 0) initWatsonFlow();
  }, [watsonContext.dialogs, openedFolders]);

  /** Efeito depois que um dialogo foi criado */
  useEffect(() => {
    if (createdDialogId) {
      setSelectedDialog(flow[createdDialogId]);
      setNodeOnFocus(createdDialogId);
    }
  }, [createdDialogId]);

  /** Efeito depois que o contexto de salvamento foi alterado */
  useEffect(() => {
    if (saveContext.savingStatus) props.renderBreadcrumbs();
  }, [saveContext]);

  setFlowNodeActionHandlers();

  if (loading) return <Loading />;
  if (isWatsonLoadingFailed) return showWatsonLoadingFailed();

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <CanvasFlow
        removeElementFlow={() => null}
        selectedNodes={selectedDialog ? [selectedDialog.id] : [null]}
        flow={flow}
        selectNode={selectNodeHandler}
        nodeOnFocus={nodeOnFocus}
        setNodeOnFocus={setNodeOnFocus}
      />
      {showWatsonConfigurations()}
    </div>
  );
};

export default WatsonFlow;
